import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Card, CardContent, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, TextField, Select, MenuItem, FormControl, InputLabel,
  IconButton, Tooltip, Alert, Snackbar, CircularProgress, TablePagination,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  Refresh, CheckCircle, Error as ErrorIcon, HourglassEmpty,
  People, Settings, Replay, TrendingUp, ArrowUpward, ArrowDownward,
  Add, ContentCopy, Delete, Link as LinkIcon, ArrowBack, Visibility
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { adminAPI } from '../services/api';

// ==================== TOWER TON TAB ====================
const TowerTab = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 25, offset: 0 });
  const [settings, setSettings] = useState({ tower_merchant_id: '', tower_api_mode: 'test' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadStats = async () => {
    try {
      const res = await adminAPI.getPartnerStats();
      if (res.data.success) setStats(res.data.data);
    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
    }
  };

  const loadUsers = async (offset = 0, filter = statusFilter, sort = sortBy, order = sortOrder) => {
    try {
      const res = await adminAPI.getPartnerUsers({ limit: pagination.limit, offset, status: filter, sortBy: sort, sortOrder: order });
      if (res.data.success) {
        setUsers(res.data.data);
        setPagination(prev => ({ ...prev, total: res.data.pagination.total, offset }));
      }
    } catch (error) {
      console.error('Ошибка загрузки юзеров:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await adminAPI.getPartnerSettings();
      if (res.data.success) setSettings(res.data.data);
    } catch (error) {
      console.error('Ошибка загрузки настроек:', error);
    }
  };

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadStats(), loadUsers(0), loadSettings()]);
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line
  useEffect(() => { loadUsers(0, statusFilter); }, [statusFilter]); // eslint-disable-line

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const res = await adminAPI.savePartnerSettings(settings);
      if (res.data.success) setSnackbar({ open: true, message: 'Настройки сохранены', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка сохранения', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleRetry = async (id) => {
    try {
      const res = await adminAPI.retryPartnerApprove(id);
      if (res.data.success) {
        setSnackbar({
          open: true,
          message: res.data.data.success ? 'Approve отправлен' : `Ошибка: ${res.data.data.error}`,
          severity: res.data.data.success ? 'success' : 'warning'
        });
        loadUsers(pagination.offset);
        loadStats();
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка retry', severity: 'error' });
    }
  };

  const handleSort = (column) => {
    const newOrder = (sortBy === column && sortOrder === 'desc') ? 'asc' : 'desc';
    setSortBy(column);
    setSortOrder(newOrder);
    loadUsers(0, statusFilter, column, newOrder);
  };

  const SortHeader = ({ column, children }) => (
    <TableCell onClick={() => handleSort(column)} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', '&:hover': { color: '#90caf9' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {children}
        {sortBy === column && (sortOrder === 'asc' ? <ArrowUpward sx={{ fontSize: 16 }} /> : <ArrowDownward sx={{ fontSize: 16 }} />)}
      </Box>
    </TableCell>
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusChip = (row) => {
    if (row.approve_success === true) return <Chip icon={<CheckCircle />} label="Approved" color="success" size="small" />;
    if (row.approve_sent && !row.approve_success) return <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />;
    return <Chip icon={<HourglassEmpty />} label="Pending" color="warning" size="small" />;
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  const summary = stats?.summary || { total: 0, approved: 0, failed: 0, pending: 0, converted: 0 };
  const dailyData = (stats?.daily || []).map(d => ({
    date: new Date(d.date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
    count: d.count
  }));
  const fmtAmount = (v) => { const n = parseFloat(v) || 0; return n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n.toFixed(1); };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button startIcon={<Refresh />} variant="outlined" size="small" onClick={loadAll}>Обновить</Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Всего', value: summary.total, icon: <People sx={{ color: '#64b5f6' }} />, bg: '#1e3a5f', color: '#90caf9' },
          { label: 'Approved', value: summary.approved, icon: <CheckCircle sx={{ color: '#81c784' }} />, bg: '#1b5e20', color: '#a5d6a7' },
          { label: 'Failed', value: summary.failed, icon: <ErrorIcon sx={{ color: '#ef9a9a' }} />, bg: '#b71c1c', color: '#ef9a9a' },
          { label: 'Pending', value: summary.pending, icon: <HourglassEmpty sx={{ color: '#ffcc80' }} />, bg: '#e65100', color: '#ffcc80' },
          { label: 'Конверсия', value: `${summary.converted} (${summary.total > 0 ? Math.round(summary.converted / summary.total * 100) : 0}%)`, icon: <TrendingUp sx={{ color: '#ce93d8' }} />, bg: '#4a148c', color: '#ce93d8' },
        ].map((c, i) => (
          <Grid item xs={6} md={2.4} key={i}>
            <Card sx={{ background: `linear-gradient(135deg, ${c.bg} 0%, ${c.bg}dd 100%)` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>{c.icon}<Typography variant="body2" sx={{ color: c.color }}>{c.label}</Typography></Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{c.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Settings sx={{ color: '#ffd700' }} />
          <Typography variant="h6">Настройки Tower TON</Typography>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField fullWidth label="Merchant ID" placeholder="cf433aff-c173-4503-92ea-957d241372fe" value={settings.tower_merchant_id || ''} onChange={(e) => setSettings(prev => ({ ...prev, tower_merchant_id: e.target.value }))} size="small" />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>API Mode</InputLabel>
              <Select value={settings.tower_api_mode || 'test'} label="API Mode" onChange={(e) => setSettings(prev => ({ ...prev, tower_api_mode: e.target.value }))}>
                <MenuItem value="test">Test</MenuItem>
                <MenuItem value="production">Production</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="contained" onClick={handleSaveSettings} disabled={saving} fullWidth>{saving ? <CircularProgress size={20} /> : 'Сохранить'}</Button>
          </Grid>
        </Grid>
      </Paper>

      {dailyData.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Юзеры по дням (30 дней)</Typography>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
              <YAxis stroke="#9ca3af" fontSize={12} />
              <RechartsTooltip contentStyle={{ background: '#1e293b', border: '1px solid #374151', borderRadius: 8 }} />
              <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} name="Юзеры" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      <Paper sx={{ p: { xs: 1.5, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h6">Юзеры от Tower TON</Typography>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Статус</InputLabel>
            <Select value={statusFilter} label="Статус" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="all">Все</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortHeader column="telegram_id">Telegram ID</SortHeader>
                <SortHeader column="username">Username</SortHeader>
                <SortHeader column="first_name">Имя</SortHeader>
                <SortHeader column="created_at">Дата</SortHeader>
                <SortHeader column="approve_success">Статус</SortHeader>
                <SortHeader column="deposit_count">Конверсия</SortHeader>
                <SortHeader column="deposit_total">Депозиты</SortHeader>
                <SortHeader column="staking_total">Стейкинг</SortHeader>
                <SortHeader column="slots_total">Слоты</SortHeader>
                <SortHeader column="retry_count">Retries</SortHeader>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: '#6b7280' }}>Нет данных</TableCell></TableRow>
              ) : users.map((row) => {
                const isConverted = (row.deposit_count > 0) || (row.staking_count > 0);
                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{row.telegram_id}</TableCell>
                    <TableCell>{row.username ? `@${row.username}` : '—'}</TableCell>
                    <TableCell>{row.first_name || '—'}</TableCell>
                    <TableCell>{formatDate(row.created_at)}</TableCell>
                    <TableCell>{getStatusChip(row)}</TableCell>
                    <TableCell><Chip label={isConverted ? 'Да' : 'Нет'} color={isConverted ? 'success' : 'default'} size="small" variant={isConverted ? 'filled' : 'outlined'} /></TableCell>
                    <TableCell>{row.deposit_count > 0 ? <Typography variant="body2">{row.deposit_count} / {fmtAmount(row.deposit_total)} SPERM</Typography> : '—'}</TableCell>
                    <TableCell>{row.staking_count > 0 ? <Typography variant="body2">{row.staking_count} / {fmtAmount(row.staking_total)} SPERM</Typography> : '—'}</TableCell>
                    <TableCell>{row.slots_count > 0 ? <Typography variant="body2">{row.slots_count} / {fmtAmount(row.slots_total)} SPERM</Typography> : '—'}</TableCell>
                    <TableCell>{row.retry_count}</TableCell>
                    <TableCell align="right">
                      {row.approve_success !== true && (
                        <Tooltip title="Повторить approve"><IconButton size="small" color="primary" onClick={() => handleRetry(row.id)}><Replay /></IconButton></Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={pagination.total} page={Math.floor(pagination.offset / pagination.limit)} onPageChange={(e, p) => loadUsers(p * pagination.limit)} rowsPerPage={pagination.limit} rowsPerPageOptions={[25]} labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`} />
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

// ==================== TRAFFIC LINKS TAB ====================
const TrafficLinksTab = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  // Детали ссылки
  const [detailLink, setDetailLink] = useState(null);
  const [detailUsers, setDetailUsers] = useState([]);
  const [detailPagination, setDetailPagination] = useState({ total: 0, limit: 50, offset: 0 });
  const [detailLoading, setDetailLoading] = useState(false);
  const [convertedOnly, setConvertedOnly] = useState(false);

  const loadLinks = async (sort = sortBy, order = sortOrder) => {
    setLoading(true);
    try {
      const res = await adminAPI.getTrafficLinks({ sortBy: sort, sortOrder: order });
      if (res.data.success) setLinks(res.data.data);
    } catch (error) {
      console.error('Ошибка загрузки ссылок:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLinks(); }, []); // eslint-disable-line

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await adminAPI.createTrafficLink({ name: newName.trim() });
      if (res.data.success) {
        setSnackbar({ open: true, message: 'Ссылка создана', severity: 'success' });
        setCreateOpen(false);
        setNewName('');
        loadLinks();
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка создания', severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Удалить ссылку "${name}"?`)) return;
    try {
      const res = await adminAPI.deleteTrafficLink(id);
      if (res.data.success) {
        setSnackbar({ open: true, message: 'Ссылка удалена', severity: 'success' });
        loadLinks();
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка удаления', severity: 'error' });
    }
  };

  const copyLink = (code) => {
    const link = `https://t.me/moonraid_game_bot?start=${code}`;
    navigator.clipboard.writeText(link);
    setSnackbar({ open: true, message: 'Ссылка скопирована', severity: 'success' });
  };

  const handleSort = (column) => {
    const newOrder = (sortBy === column && sortOrder === 'desc') ? 'asc' : 'desc';
    setSortBy(column);
    setSortOrder(newOrder);
    loadLinks(column, newOrder);
  };

  const SortHeader = ({ column, children }) => (
    <TableCell onClick={() => handleSort(column)} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', '&:hover': { color: '#90caf9' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {children}
        {sortBy === column && (sortOrder === 'asc' ? <ArrowUpward sx={{ fontSize: 16 }} /> : <ArrowDownward sx={{ fontSize: 16 }} />)}
      </Box>
    </TableCell>
  );

  // Загрузка деталей ссылки
  const openDetails = async (link) => {
    setDetailLink(link);
    setDetailLoading(true);
    setConvertedOnly(false);
    try {
      const res = await adminAPI.getTrafficLinkDetails(link.id, { limit: 50, offset: 0, convertedOnly: 'false' });
      if (res.data.success) {
        setDetailUsers(res.data.data.users);
        setDetailPagination(res.data.data.pagination);
      }
    } catch (error) {
      console.error('Ошибка загрузки деталей:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadDetailUsers = async (offset = 0, onlyConverted = convertedOnly) => {
    setDetailLoading(true);
    try {
      const res = await adminAPI.getTrafficLinkDetails(detailLink.id, { limit: 50, offset, convertedOnly: String(onlyConverted) });
      if (res.data.success) {
        setDetailUsers(res.data.data.users);
        setDetailPagination(res.data.data.pagination);
      }
    } catch (error) {
      console.error('Ошибка загрузки юзеров:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const fmtAmount = (v) => { const n = parseFloat(v) || 0; return n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n.toFixed(1); };

  // Детальный вид ссылки
  if (detailLink) {
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => setDetailLink(null)}><ArrowBack /></IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{detailLink.name}</Typography>
          <Chip label={`t.me/...?start=${detailLink.code}`} size="small" sx={{ fontFamily: 'monospace' }} />
          <Box sx={{ flex: 1 }} />
          <Button
            variant={convertedOnly ? 'contained' : 'outlined'}
            size="small"
            color="success"
            onClick={() => {
              const next = !convertedOnly;
              setConvertedOnly(next);
              loadDetailUsers(0, next);
            }}
          >
            {convertedOnly ? 'Только конверсия' : 'Все юзеры'}
          </Button>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={4}>
            <Card sx={{ background: 'linear-gradient(135deg, #1e3a5f, #2d5a87)' }}>
              <CardContent>
                <Typography variant="body2" sx={{ color: '#90caf9' }}>Визиты</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{detailLink.visits}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card sx={{ background: 'linear-gradient(135deg, #1b5e20, #2e7d32)' }}>
              <CardContent>
                <Typography variant="body2" sx={{ color: '#a5d6a7' }}>Конверсия</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{detailLink.converted}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card sx={{ background: 'linear-gradient(135deg, #4a148c, #6a1b9a)' }}>
              <CardContent>
                <Typography variant="body2" sx={{ color: '#ce93d8' }}>% конверсии</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {detailLink.visits > 0 ? Math.round(detailLink.converted / detailLink.visits * 100) : 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: { xs: 1.5, sm: 3 } }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Telegram ID</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Имя</TableCell>
                      <TableCell>Дата визита</TableCell>
                      <TableCell>Конверсия</TableCell>
                      <TableCell>Депозиты</TableCell>
                      <TableCell>Стейкинг</TableCell>
                      <TableCell>Слоты</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailUsers.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: '#6b7280' }}>Нет данных</TableCell></TableRow>
                    ) : detailUsers.map((row, i) => {
                      const isConverted = (row.deposit_count > 0) || (row.staking_count > 0) || (row.slots_count > 0);
                      return (
                        <TableRow key={i} hover sx={isConverted ? { background: 'rgba(76,175,80,0.08)' } : {}}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{row.telegram_id}</TableCell>
                          <TableCell>{row.username ? `@${row.username}` : '—'}</TableCell>
                          <TableCell>{row.first_name || '—'}</TableCell>
                          <TableCell>{formatDate(row.visited_at)}</TableCell>
                          <TableCell>
                            <Chip label={isConverted ? 'Да' : 'Нет'} color={isConverted ? 'success' : 'default'} size="small" variant={isConverted ? 'filled' : 'outlined'} />
                          </TableCell>
                          <TableCell>{row.deposit_count > 0 ? `${row.deposit_count} / ${fmtAmount(row.deposit_total)} SPERM` : '—'}</TableCell>
                          <TableCell>{row.staking_count > 0 ? `${row.staking_count} / ${fmtAmount(row.staking_total)} SPERM` : '—'}</TableCell>
                          <TableCell>{row.slots_count > 0 ? `${row.slots_count} / ${fmtAmount(row.slots_total)} SPERM` : '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={detailPagination.total}
                page={Math.floor(detailPagination.offset / detailPagination.limit)}
                onPageChange={(e, p) => loadDetailUsers(p * detailPagination.limit)}
                rowsPerPage={detailPagination.limit}
                rowsPerPageOptions={[50]}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
              />
            </>
          )}
        </Paper>

        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </>
    );
  }

  // Список ссылок
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Box />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<Add />} variant="contained" onClick={() => setCreateOpen(true)}>Создать ссылку</Button>
          <Button startIcon={<Refresh />} variant="outlined" size="small" onClick={() => loadLinks()}>Обновить</Button>
        </Box>
      </Box>

      {links.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <LinkIcon sx={{ fontSize: 48, color: '#6b7280', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#9ca3af', mb: 1 }}>Нет трафик-ссылок</Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>Создайте первую ссылку для отслеживания источников трафика</Typography>
          <Button startIcon={<Add />} variant="contained" onClick={() => setCreateOpen(true)}>Создать ссылку</Button>
        </Paper>
      ) : (
        <Paper sx={{ p: { xs: 1.5, sm: 3 } }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortHeader column="name">Название</SortHeader>
                  <TableCell>Ссылка</TableCell>
                  <SortHeader column="visits">Визиты</SortHeader>
                  <SortHeader column="converted">Конверсия</SortHeader>
                  <TableCell>% конверсии</TableCell>
                  <SortHeader column="created_at">Создана</SortHeader>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {links.map((link) => (
                  <TableRow key={link.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{link.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#90caf9' }}>
                          t.me/...?start={link.code}
                        </Typography>
                        <Tooltip title="Копировать ссылку">
                          <IconButton size="small" onClick={() => copyLink(link.code)}><ContentCopy sx={{ fontSize: 16 }} /></IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{link.visits}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={link.converted}
                        color={link.converted > 0 ? 'success' : 'default'}
                        size="small"
                        variant={link.converted > 0 ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: link.converted > 0 ? '#4caf50' : '#6b7280' }}>
                        {link.visits > 0 ? Math.round(link.converted / link.visits * 100) : 0}%
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(link.created_at)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Подробнее">
                          <IconButton size="small" color="primary" onClick={() => openDetails(link)}><Visibility /></IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить">
                          <IconButton size="small" color="error" onClick={() => handleDelete(link.id, link.name)}><Delete /></IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Диалог создания */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Создать трафик-ссылку</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Название (для тебя)"
            placeholder="YouTube март, Twitter реклама..."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            sx={{ mt: 1 }}
          />
          <Typography variant="body2" sx={{ color: '#6b7280', mt: 1 }}>
            Будет сгенерирована уникальная ссылка вида t.me/moonraid_game_bot?start=t_xxxxxxxx
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !newName.trim()}>
            {creating ? <CircularProgress size={20} /> : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

// ==================== MAIN COMPONENT ====================
const Partners = () => {
  const [tab, setTab] = useState(1); // По умолчанию открываем Трафик-ссылки

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>Партнёры</Typography>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 3 }}>
        <Tab label="Tower TON (CPA)" />
        <Tab label="Трафик-ссылки" />
      </Tabs>

      {tab === 0 && <TowerTab />}
      {tab === 1 && <TrafficLinksTab />}
    </Box>
  );
};

export default Partners;
